import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listScenarios, shareScenario, createShareLink, deleteShareLink, fmt, fmtPct, fmtNum } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Calculator, TrendingUp, Percent, Building2, ArrowRight, Share2, Users, X, Link2, Copy, Check, Trash2 } from "lucide-react";

export default function Dashboard() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [linkOpen, setLinkOpen] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const data = await listScenarios();
      setScenarios(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (scenarioId) => {
    if (!shareEmail.trim()) return;
    try {
      await shareScenario(scenarioId, shareEmail.trim().toLowerCase());
      toast.success(`Shared with ${shareEmail}`);
      setShareEmail("");
      setShareOpen(null);
      loadScenarios();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to share");
    }
  };

  const handleCreateLink = async (scenarioId) => {
    setLinkLoading(true);
    try {
      const data = await createShareLink(scenarioId);
      const url = `${window.location.origin}/shared/${data.share_token}`;
      setLinkUrl(url);
      setLinkOpen(scenarioId);
      setCopied(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(linkUrl);
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevokeLink = async (scenarioId) => {
    try {
      await deleteShareLink(scenarioId);
      setLinkOpen(null);
      setLinkUrl("");
      loadScenarios();
      toast.success("Share link revoked");
    } catch (e) {
      toast.error("Failed to revoke link");
    }
  };

  const latest = scenarios[0];
  const kpis = latest?.results?.kpis;

  const kpiCards = kpis
    ? [
        { label: "Gross Dev Value", value: fmt(kpis.total_gdv), sub: `${fmtNum(kpis.total_units)} units`, icon: Building2 },
        { label: "Dev Profit", value: fmt(kpis.dev_profit), sub: `${fmtPct(kpis.dev_profit_on_cost)} on cost`, icon: TrendingUp },
        { label: "Ungeared IRR", value: fmtPct(kpis.ungeared_irr), sub: `Multiple: ${kpis.ungeared_multiple?.toFixed(2)}x`, icon: Percent },
        { label: "Geared IRR", value: fmtPct(kpis.geared_irr), sub: `Multiple: ${kpis.geared_multiple?.toFixed(2)}x`, icon: Calculator },
      ]
    : [];

  return (
    <div className="page-enter" style={{ padding: "32px 40px" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 28, color: "hsl(var(--foreground))" }} data-testid="dashboard-title">
          Development Appraisal
        </h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginTop: 4 }}>
          Property development financial modelling & analysis
        </p>
      </div>

      {kpiCards.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }} data-testid="kpi-grid">
          {kpiCards.map((kpi, i) => (
            <div key={i} className="kpi-card animate-in" style={{ animationDelay: `${i * 50}ms` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="kpi-label">{kpi.label}</div>
                <kpi.icon size={16} style={{ color: "hsl(var(--accent))" }} />
              </div>
              <div className="kpi-value" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>{kpi.value}</div>
              <div className="kpi-sub">{kpi.sub}</div>
            </div>
          ))}
        </div>
      )}

      {!kpis && !loading && (
        <Card className="border-dashed" style={{ marginBottom: 32 }}>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Calculator size={40} style={{ color: "hsl(var(--muted-foreground))", marginBottom: 16 }} />
            <h3 style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 18, marginBottom: 8 }}>No appraisals yet</h3>
            <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginBottom: 20 }}>
              Create your first development appraisal to get started
            </p>
            <Button
              onClick={() => navigate("/appraisal")}
              style={{ background: "hsl(var(--foreground))", color: "white" }}
              data-testid="create-first-appraisal"
            >
              <Calculator size={16} style={{ marginRight: 8 }} />
              New Appraisal
            </Button>
          </CardContent>
        </Card>
      )}

      {scenarios.length > 0 && (
        <div>
          <div className="section-header">Saved Scenarios</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {scenarios.map((s) => (
              <div
                key={s.id}
                className="scenario-card animate-in"
                data-testid={`scenario-card-${s.id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div style={{ cursor: "pointer", flex: 1 }} onClick={() => navigate(`/appraisal/${s.id}`)}>
                    <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>
                      {s.project_name || "Untitled"}
                    </div>
                    <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                      {s.location || "No location"} &middot; {new Date(s.created_at).toLocaleDateString("en-GB")}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {s.shared_with?.length > 0 && (
                      <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", display: "flex", alignItems: "center", gap: 2 }}>
                        <Users size={12} /> {s.shared_with.length}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCreateLink(s.id); }}
                      style={{ padding: 4, borderRadius: 6, border: "none", background: s.share_token ? "hsl(var(--accent) / 0.1)" : "transparent", cursor: "pointer", color: s.share_token ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))" }}
                      title={s.share_token ? "Share link active" : "Create share link"}
                      data-testid={`link-btn-${s.id}`}
                    >
                      <Link2 size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShareOpen(shareOpen === s.id ? null : s.id); setShareEmail(""); }}
                      style={{ padding: 4, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}
                      data-testid={`share-btn-${s.id}`}
                    >
                      <Share2 size={14} />
                    </button>
                    <button
                      onClick={() => navigate(`/appraisal/${s.id}`)}
                      style={{ padding: 4, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}
                    >
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>

                {shareOpen === s.id && (
                  <div style={{ marginBottom: 12, padding: 12, background: "hsl(var(--muted) / 0.3)", borderRadius: 8 }} data-testid={`share-panel-${s.id}`}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <Input
                        placeholder="colleague@email.com"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleShare(s.id)}
                        style={{ flex: 1, height: 32, fontSize: 12 }}
                        data-testid={`share-email-${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button size="sm" style={{ height: 32, fontSize: 12 }} onClick={(e) => { e.stopPropagation(); handleShare(s.id); }} data-testid={`share-confirm-${s.id}`}>
                        Share
                      </Button>
                    </div>
                    {s.shared_with?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {s.shared_with.map((email) => (
                          <span key={email} style={{ fontSize: 11, background: "hsl(var(--background))", padding: "2px 8px", borderRadius: 12, display: "flex", alignItems: "center", gap: 4 }}>
                            {email}
                            <X size={10} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); /* unshare would go here */ }} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {linkOpen === s.id && (
                  <div style={{ marginBottom: 12, padding: 12, background: "hsl(var(--accent) / 0.06)", borderRadius: 8, border: "1px solid hsl(var(--accent) / 0.15)" }} data-testid={`link-panel-${s.id}`} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "hsl(var(--foreground))" }}>Quick-Share Link</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Input
                        readOnly
                        value={linkUrl}
                        style={{ flex: 1, height: 32, fontSize: 11, fontFamily: "monospace", background: "hsl(var(--background))" }}
                        data-testid={`link-url-${s.id}`}
                        onClick={(e) => e.target.select()}
                      />
                      <Button size="sm" variant="outline" style={{ height: 32, fontSize: 12, gap: 4 }} onClick={handleCopyLink} data-testid={`copy-link-${s.id}`}>
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                      <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Anyone with this link can view</span>
                      <button
                        onClick={() => handleRevokeLink(s.id)}
                        style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                        data-testid={`revoke-link-${s.id}`}
                      >
                        <Trash2 size={11} /> Revoke
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, cursor: "pointer" }} onClick={() => navigate(`/appraisal/${s.id}`)}>
                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>GDV</div>
                    <div className="data-cell" style={{ fontWeight: 500 }}>{fmt(s.results?.kpis?.total_gdv)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>Profit</div>
                    <div className="data-cell" style={{ fontWeight: 500 }}>{fmt(s.results?.kpis?.dev_profit)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>IRR (UG)</div>
                    <div className="data-cell" style={{ fontWeight: 500 }}>{fmtPct(s.results?.kpis?.ungeared_irr)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>Units</div>
                    <div className="data-cell" style={{ fontWeight: 500 }}>{s.results?.kpis?.total_units || 0}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
